import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../component/Header";
import Sidebar from "../../component/Sidebar";
const apiUrl = process.env.REACT_APP_API_URL;

const parseJSON = (data) => {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};
const getDifferences = (oldData, newData, parentKey = "") => {
  const diffs = [];
  const allKeys = new Set([
    ...Object.keys(oldData || {}),
    ...Object.keys(newData || {}),
  ]);

  allKeys.forEach((key) => {
    const oldVal = oldData?.[key];
    const newVal = newData?.[key];
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (
      typeof oldVal === "object" &&
      typeof newVal === "object" &&
      oldVal &&
      newVal
    ) {
      // Recursively diff nested objects
      diffs.push(...getDifferences(oldVal, newVal, fullKey));
    } else if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      // Compare arrays by index
      const maxLen = Math.max(oldVal.length, newVal.length);
      for (let i = 0; i < maxLen; i++) {
        if (JSON.stringify(oldVal[i]) !== JSON.stringify(newVal[i])) {
          diffs.push({
            key: `${fullKey}[${i}]`,
            oldVal: oldVal[i],
            newVal: newVal[i],
          });
        }
      }
    } else if (oldVal !== newVal) {
      diffs.push({ key: fullKey, oldVal, newVal });
    }
  });

  return diffs;
};

const actionStyles = {
  create: "bg-green-100 text-green-700 border-green-300",
  edit: "bg-blue-100 text-blue-700 border-blue-300",
  delete: "bg-red-100 text-red-700 border-red-300",
};

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("");
  const [editorFilter, setEditorFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${apiUrl}/history`);
        setHistory(res.data);
      } catch (err) {
        console.error("Error fetching history:", err);
      }
    };
    fetchHistory();
  }, []);

  const filteredHistory = history.filter((h) => {
    if (filter !== "all" && h.action !== filter) return false;
    if (
      orderFilter &&
      !h.order_number?.toLowerCase().includes(orderFilter.toLowerCase())
    )
      return false;
    if (
      editorFilter &&
      !h.user_name?.toLowerCase().includes(editorFilter.toLowerCase())
    )
      return false;
    if (dateFilter) {
      const itemDate = new Date(h.timestamp).toISOString().split("T")[0];
      if (itemDate !== dateFilter) return false;
    }
    return true;
  });

  return (
    <>
      <Header />
      <Sidebar />
      <div className="p-6 bg-gray-50 min-h-screen">
        <h1 className="text-3xl font-bold mb-6 text-gray-800">
          📜 Change History
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filter Panel */}
          <div className="bg-white rounded-xl shadow-md p-4 border lg:sticky lg:top-6 h-fit">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
              Filters
            </h2>

            <div className="flex flex-col gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Action</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="all">All</option>
                  <option value="create">Create</option>
                  <option value="edit">Edit</option>
                  <option value="delete">Delete</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Order #
                </label>
                <input
                  type="text"
                  placeholder="Search order number"
                  value={orderFilter}
                  onChange={(e) => setOrderFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Editor</label>
                <input
                  type="text"
                  placeholder="Search editor"
                  value={editorFilter}
                  onChange={(e) => setEditorFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <button
                onClick={() => {
                  setFilter("all");
                  setOrderFilter("");
                  setEditorFilter("");
                  setDateFilter("");
                }}
                className="w-full px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* History Timeline */}
          <div className="lg:col-span-3">
            {filteredHistory.length === 0 ? (
              <p className="text-gray-500 text-center mt-20">
                No history records found.
              </p>
            ) : (
              <div className="relative border-l-2 border-gray-200 pl-6">
                {filteredHistory.map((h, index) => {
                  const oldData = parseJSON(h.old_data);
                  const newData = parseJSON(h.new_data);
                  const diffs =
                    h.action === "edit" ? getDifferences(oldData, newData) : [];

                  return (
                    <div key={index} className="mb-8 relative">
                      {/* timeline dot */}
                      <span className="absolute -left-[9px] top-5 w-4 h-4 rounded-full bg-gray-400"></span>

                      <div
                        className={`p-5 rounded-xl border shadow-sm transition ${
                          actionStyles[h.action] || "bg-gray-100"
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">
                            {new Date(h.timestamp).toLocaleString()}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full border ${
                              actionStyles[h.action]
                            }`}
                          >
                            {h.action.toUpperCase()}
                          </span>
                        </div>

                        <div className="text-md font-medium text-gray-800">
                          <strong>{h.user_name}</strong> {h.action}{" "}
                          <strong>{h.order_number}</strong>
                        </div>

                        {h.action === "edit" && diffs.length > 0 && (
                          <ul className="mt-3 text-sm space-y-1">
                            {diffs.map((d, i) => (
                              <li key={i} className="flex flex-wrap gap-2">
                                <span className="font-semibold">{d.key}:</span>
                                <span className="text-red-600 line-through">
                                  {typeof d.oldVal === "object"
                                    ? JSON.stringify(d.oldVal)
                                    : d.oldVal || "—"}
                                </span>
                                <span className="text-green-600">
                                  →{" "}
                                  {typeof d.newVal === "object"
                                    ? JSON.stringify(d.newVal)
                                    : d.newVal || "—"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {h.action === "delete" && (
                          <p className="mt-2 text-sm text-red-700">
                            Row deleted
                          </p>
                        )}

                        {h.action === "create" && (
                          <p className="mt-2 text-sm text-green-700">
                            Row created
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HistoryPage;
